import { c as _c } from "react/compiler-runtime";
import { IcoGlobe16, IcoGlobe24, type Icon, IcoUserDuplicate16, IcoUserDuplicate24, IcoWindowCursor16, IcoWindowCursor24 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import Image from 'next/image';
import { useState } from 'react';
import squashKind from './squash-kind';
const PROVIDERS = ['baselayer', 'comply_advantage', 'grail_pay', 'idology', 'incode', 'lexis', 'sentilink', 'sumsub', 'transunion', 'trustfull', 'twilio', 'unit21'] as const;
const RENAMED: Record<string, string> = {
  trans_union: 'transunion'
};
const BY_LENGTH = [...PROVIDERS].sort((a, b) => squashKind(b).length - squashKind(a).length);
const providerForTool = (toolKind: string): string | undefined => {
  const squashed = squashKind(toolKind);
  const renamed = Object.keys(RENAMED).find(prefix => squashed.startsWith(squashKind(prefix)));
  if (renamed) return RENAMED[renamed];
  return BY_LENGTH.find(provider => squashed.startsWith(squashKind(provider)));
};
const FULL_BLEED = new Set(['baselayer', 'comply_advantage', 'grail_pay', 'idology', 'trustfull', 'twilio', 'unit21']);
const NEEDS_PADDING = new Set(['incode', 'sentilink', 'sumsub']);
type VendorLogoFit = 'fill' | 'padded' | 'contain';
const fitFor = (provider: string): VendorLogoFit => {
  if (FULL_BLEED.has(provider)) return 'fill';
  if (NEEDS_PADDING.has(provider)) return 'padded';
  return 'contain';
};

// Takes a tool kind (`sumsub_applicant_fetch`) or a vendor (`grail_pay`): both start with the vendor's name.
const getVendorLogo = (kind: string) => {
  const provider = providerForTool(kind);
  return provider ? {
    src: `/logos/vendors/${provider}.png`,
    fit: fitFor(provider)
  } : undefined;
};
type VendorLogoSize = 'compact' | 'default';
const browserIcons = {
  compact: IcoWindowCursor16,
  default: IcoWindowCursor24
};
const duplicatesIcons = {
  compact: IcoUserDuplicate16,
  default: IcoUserDuplicate24
};
const webResearchIcons = {
  compact: IcoGlobe16,
  default: IcoGlobe24
};

// Keyed by display name as well as kind: an agent's summary lists its tools by name only.
const OWN_TOOL_ICONS: Record<string, Record<VendorLogoSize, Icon>> = {
  browser: browserIcons,
  browser_use: browserIcons,
  duplicate_entities: duplicatesIcons,
  entity_duplicates: duplicatesIcons,
  osint: webResearchIcons,
  web_research: webResearchIcons
};
const ownToolIconsByKind = new Map(Object.entries(OWN_TOOL_ICONS).map(([kind, icons]) => [squashKind(kind), icons]));
const ownToolIcon = (kind: string, size: VendorLogoSize) => ownToolIconsByKind.get(squashKind(kind))?.[size];
const BOX = 'flex shrink-0 items-center justify-center overflow-hidden';
const VendorLogo = t0 => {
  const $ = _c(25);
  const {
    toolKind,
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? "compact" : t1;
  const box = `${BOX} ${size === "compact" ? "size-5 rounded-sm" : "size-8 rounded-md"}`;
  const [failed, setFailed] = useState(false);
  let t2;
  if ($[0] !== toolKind) {
    t2 = getVendorLogo(toolKind);
    $[0] = toolKind;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const logo = t2;
  if (!logo || failed) {
    let t3;
    if ($[2] !== size || $[3] !== toolKind) {
      t3 = ownToolIcon(toolKind, size);
      $[2] = size;
      $[3] = toolKind;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const OwnIcon = t3;
    const t4 = `${box} border border-tertiary bg-primary text-caption-1 text-tertiary`;
    let t5;
    if ($[5] !== OwnIcon || $[6] !== name) {
      t5 = OwnIcon ? <OwnIcon color="primary" /> : name.charAt(0).toUpperCase();
      $[5] = OwnIcon;
      $[6] = name;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== t4 || $[9] !== t5) {
      t6 = <span aria-hidden={true} className={t4}>{t5}</span>;
      $[8] = t4;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  const padded = logo.fit === "padded";
  const t3 = `${box} ${logo.fit === "fill" ? "" : "border border-tertiary bg-primary"}`;
  const t4 = logo.src;
  const t5 = size === "compact" ? 20 : 32;
  const t6 = size === "compact" ? 20 : 32;
  const t7 = size === "compact" && padded;
  const t8 = size === "compact" && !padded;
  const t9 = size === "default" && padded;
  const t10 = size === "default" && !padded;
  let t11;
  if ($[11] !== t10 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t11 = cx("object-contain", {
      "size-4": t7,
      "size-5": t8,
      "size-6": t9,
      "size-8": t10
    });
    $[11] = t10;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setFailed(true);
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== logo.src || $[18] !== t11 || $[19] !== t5 || $[20] !== t6) {
    t13 = <Image src={t4} alt="" width={t5} height={t6} className={t11} onError={t12} unoptimized={true} />;
    $[17] = logo.src;
    $[18] = t11;
    $[19] = t5;
    $[20] = t6;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t3) {
    t14 = <span className={t3}>{t13}</span>;
    $[22] = t13;
    $[23] = t3;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
};
export type { VendorLogoFit };
export { getVendorLogo, ownToolIcon };
export default VendorLogo;